import { memo, useCallback, useEffect } from 'react';
import { View, Text, Image, Pressable, StyleSheet, Clipboard } from 'react-native';
import { I18n } from '../components';

import StyledInput from './styled_input';
import CommonStyles from './common_styles';
import { COLOR, FONT, FONT_SIZE, THEME, __WEB__ } from '../../constants/styles';

const MFAComponent = (props, rendered) => {
  const onSubmit = props.onSubmit;
  const mfaToken = props.mfaToken;

  useEffect(() => {
    if (mfaToken?.trim().length === 6) onSubmit();
  }, [mfaToken, onSubmit]);

  return rendered;
};

const SetupMFA = (props) => {
  const secret = props.secret;
  const handleCopyPress = useCallback(
    function () {
      if (__WEB__) navigator.clipboard.writeText(secret);
      else Clipboard.setString(secret);
    },
    [secret]
  );

  const rendered = (
    <View style={styles.inputsContainer}>
      <Text style={styles.header}>{I18n.t('auth.2faTitle')}</Text>

      <Text style={styles.descText}>{I18n.t('auth.2faDesc1')}</Text>

      <View style={styles.imageContainer}>
        <Image style={styles.qrImage} source={{ uri: props.qrcode }} />
      </View>

      <Text style={styles.descText}>{I18n.t('auth.2faDesc2')}</Text>

      <View style={[THEME.basicRow, { marginBottom: __WEB__ ? 25 : 5 }]}>
        <Text style={styles.setupKeyText}>{props.secret}</Text>
        <Pressable onPress={handleCopyPress} style={styles.copyBtn}>
          <Text style={THEME.linkStyle}>Copy</Text>
        </Pressable>
      </View>

      <StyledInput
        label={I18n.ac('auth.2faFieldLabel')}
        value={props.mfaToken}
        onChangeText={props.handleCodeChange}
        keyboardType="number-pad"
        testID="authCode"
        containerStyle={{}}
      />

      {props.renderedError}

      {/*
      <View style={{ flexDirection: 'row' }}>
        <View style={{ flex: 0.5 }}>
          <CTAButton onPress={props.onSubmit} label={I18n.t('auth.login')} />
        </View>
      </View>
      */}

      {!__WEB__ && (
        <Pressable style={styles.resetMFAButton} onPress={props.handleNoAuthAppPress}>
          <Text style={styles.resetMFAButtonText}>
            {I18n.t('auth.2faNoAuthAppLabel1')}
            <Text style={THEME.linkStyle}> {I18n.t('auth.2faNoAuthAppLabel2')}</Text>
          </Text>
        </Pressable>
      )}
    </View>
  );

  return MFAComponent(props, rendered);
};

const EnterMFA = (props) => {
  const rendered = (
    <View style={styles.inputsContainer}>
      <Text style={styles.header}>{I18n.t('auth.2faTitle')}</Text>

      <Text style={[styles.descText, { marginBottom: 25 }]}>{I18n.t('auth.2faDesc1')}</Text>

      <StyledInput
        label={I18n.ac('auth.2faFieldLabel')}
        value={props.mfaToken}
        onChangeText={props.handleCodeChange}
        keyboardType="number-pad"
        testID="authCode"
        containerStyle={{}}
      />

      {props.renderedError}

      {/*
      <View style={{ flexDirection: 'row' }}>
        <View style={{ flex: 0.5 }}>
          <CTAButton onPress={props.onSubmit} label={I18n.t('auth.login')} />
        </View>
      </View>
      */}

      {props.email && props.password && !__WEB__ && (
        <Pressable style={styles.resetMFAButton} onPress={props.handleReset2FAPress}>
          <Text style={styles.resetMFAButtonText}>
            {I18n.t('auth.2faResetInstructionsLabel1')}
            <Text style={THEME.linkStyle}> {I18n.t('auth.2faResetInstructionsLabel2Native')}</Text>
          </Text>
        </Pressable>
      )}
      {props.email && props.password && !!__WEB__ && (
        <Pressable style={styles.resetMFAButton} onPress={props.handleReset2FAPress}>
          <View style={styles.resetMFAIconContainer}>
            <Image
              style={styles.resetMFAIcon}
              source={require('../../../resources/icon-repeat.png')}
            />
          </View>
          <Text style={styles.resetMFAButtonText}>
            {I18n.t('auth.2faResetInstructionsLabel1')}
            <Text style={THEME.linkStyle}> {I18n.t('auth.2faResetInstructionsLabel2Web')}</Text>
          </Text>
        </Pressable>
      )}
      {(!props.email || !props.password) && (
        <Pressable style={styles.resetMFAButton} onPress={props.handleSupportEmailPress}>
          <Text style={styles.resetMFAButtonText}>
            {I18n.t('auth.2faAndPasswordResetInstructionsLabel1')}
            <Text style={THEME.linkStyle}>
              {' '}
              {I18n.t('auth.2faAndPasswordResetInstructionsLabel2')}
            </Text>
          </Text>
        </Pressable>
      )}
    </View>
  );

  return MFAComponent(props, rendered);
};

const styles = StyleSheet.create({
  ...CommonStyles,
  header: {
    ...FONT.regular,
    fontSize: 24,
    color: __WEB__ ? COLOR.text : 'white',
    marginBottom: 25,
    marginTop: 25,
  },
  resetPasswordButton: {
    paddingVertical: 20,
    alignSelf: 'flex-start',
  },
  descText: {
    ...FONT.regular,
    fontSize: 14,
    color: __WEB__ ? COLOR.text : 'white',
  },
  setupKeyText: {
    ...FONT.medium,
    fontSize: 16,
    color: COLOR.emphasis,
  },
  copyBtn: {
    padding: 15,
  },
  imageContainer: {
    backgroundColor: COLOR.accent,
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 25,
  },
  qrImage: {
    width: 200,
    height: 200,
  },
  resetPasswordButtonText: __WEB__
    ? {
        ...FONT.light,
        fontSize: FONT_SIZE.small,
        color: COLOR.callToAction,
      }
    : {
        ...FONT.medium,
        fontSize: FONT_SIZE.large,
        color: COLOR.callToAction,
      },
  resetMFAButton: {
    ...THEME.basicRow,
  },
  resetMFAButtonText: {
    ...FONT.regular,
    color: __WEB__ ? COLOR.text : COLOR.darkTheme.text,
  },
  resetMFAIconContainer: {
    backgroundColor: COLOR.accent,
    padding: 7,
    borderRadius: 8,
    marginRight: 15,
  },
  resetMFAIcon: {
    tintColor: COLOR.textLight,
    width: 20,
    height: 20,
  },
});

module.exports = {
  SetupMFA: memo(SetupMFA),
  EnterMFA: memo(EnterMFA),
};
